import { userRoles } from './globalConstants'

export const VC_ROLE_LABELS = {
	school: 'School',
	counsellor: 'Counsellor',
	expert: 'MyPeegu Expert',
	super_admin: 'Super Admin',
}

export const VC_ROLE_COLORS = {
	school: { bg: '#ECFDF5', text: '#047857', border: '#A7F3D0' },
	counsellor: { bg: '#EEF2FF', text: '#4338CA', border: '#C7D2FE' },
	expert: { bg: '#FDF4FF', text: '#9333EA', border: '#E9D5FF' },
	super_admin: { bg: '#FFF7ED', text: '#C2410C', border: '#FED7AA' },
}

export function isAdminUser(user) {
	return (user?.permissions || []).some((p) =>
		[userRoles.admin, userRoles.superAdmin, 'Admin', 'SuperAdmin'].includes(p),
	)
}

export function resolveVcJoinRole(user, sessionCounsellorId) {
	const perms = user?.permissions || []
	const userId = String(user?._id || '')
	const sessionCid = sessionCounsellorId ? String(sessionCounsellorId) : ''

	if (sessionCid && userId && userId === sessionCid) return 'counsellor'
	if (perms.includes(userRoles.scCounselor) || perms.includes('ScCounselor')) return 'counsellor'
	if (perms.includes(userRoles.peeguCounselor) || perms.includes('PeeguCounselor')) {
		if (!sessionCid) return 'counsellor'
		return 'expert'
	}
	if (isAdminUser(user)) return 'super_admin'
	return 'counsellor'
}

export function parseIdentityRole(identity = '') {
	const lower = String(identity).toLowerCase()
	if (lower.startsWith('ghost:') || lower.startsWith('observer:')) return null
	if (lower.startsWith('student:') || lower.startsWith('school:')) return 'school'
	if (lower.startsWith('expert:')) return 'expert'
	if (lower.startsWith('superadmin:')) return 'super_admin'
	if (lower.startsWith('counsellor:')) return 'counsellor'
	if (lower.includes('peegu-ai') || lower.includes('ai-bot')) return null
	return 'unknown'
}

export function getRoleLabelFromIdentity(identity) {
	const role = parseIdentityRole(identity)
	if (!role || role === 'unknown') {
		const part = String(identity).split(':')[1]
		return part || identity || 'Participant'
	}
	return VC_ROLE_LABELS[role] || role
}

export function getDisplayNameFromIdentity(identity = '') {
	const parts = String(identity).split(':')
	if (parts.length >= 2 && parts[1] && !/^[a-f0-9]{24}$/i.test(parts[1])) return parts[1]
	if (parts.length >= 3) return parts[1]
	return getRoleLabelFromIdentity(identity)
}

export function buildCounsellorTokenExtra(user, meetingInfo = {}) {
	const role = resolveVcJoinRole(user, meetingInfo?.counsellorId)
	const displayName = user?.name || user?.email || 'Counsellor'
	return {
		participantRole: role,
		participantName: `Counsellor:${displayName}`,
		counsellorId: user?._id,
		counsellorName: displayName,
		userId: user?._id,
		userPermissions: user?.permissions || [],
		isAdmin: isAdminUser(user),
	}
}

export function buildSchoolTokenExtra(auth, schoolLabel) {
	return {
		participantRole: 'school',
		participantName: `Student:${schoolLabel}`,
		schoolId: auth?.schoolId || null,
		schoolName: schoolLabel,
		userId: auth?.schoolId || auth?._id || null,
	}
}

const JOIN_ERROR_MESSAGES = {
	ROLE_SLOT_TAKEN:
		'Another person with this role is already in the meeting. Only one can join at a time — please wait for them to leave, or join as a different role.',
	PARTICIPANT_LIMIT:
		'This meeting is full. Maximum 4 participants allowed: 1 School, 1 Counsellor, 1 MyPeegu Expert (optional), and 1 Super Admin (optional).',
	ROLE_ALREADY_CONNECTED: 'You are already connected from another tab. Close the other tab or wait a moment and try again.',
	SCHOOL_MISMATCH: 'This meeting belongs to a different school.',
	COUNSELLOR_NOT_ASSIGNED: 'Only the assigned counsellor can join as Counsellor.',
	SCHOOL_NOT_ASSIGNED: 'You are not assigned to this school.',
	EXPERT_NOT_AUTHORIZED: 'Only MyPeegu Experts can join as Expert.',
	USE_COUNSELLOR_ROLE: 'Join as Counsellor, not Expert.',
	ADMIN_NOT_AUTHORIZED: 'Only Super Admin can join as monitor.',
	INVALID_ROLE: 'Invalid role for this meeting.',
}

/**
 * Clear UX copy when a role seat is already taken.
 * Prefer backend message; otherwise use role label.
 */
export function getRoleSlotTakenMessage(role, backendMessage) {
	if (backendMessage && typeof backendMessage === 'string' && backendMessage.trim()) {
		return backendMessage.trim()
	}
	const label = VC_ROLE_LABELS[role] || 'participant'
	return `Another ${label} is already in this meeting. Only one ${label} can join at a time. Please wait for them to leave, or join as a different role.`
}

export function getParticipantJoinErrorMessage(err) {
	if (!err) return 'Could not join the meeting.'
	const msg = String(err.message || err.userMessage || '')
	const status = err.httpStatus
	const code = err.code
	const role = err.role

	if (status === 410 || /expired|has ended|meeting ended/i.test(msg)) {
		return 'This meeting link has expired. Please schedule or create a new meeting.'
	}
	if (status === 404 || /not found|invalid room/i.test(msg)) {
		return 'This meeting link is invalid or no longer exists. Please create a new meeting.'
	}

	if (code === 'ROLE_SLOT_TAKEN') {
		return getRoleSlotTakenMessage(role, msg) || JOIN_ERROR_MESSAGES.ROLE_SLOT_TAKEN
	}
	if (code === 'ROLE_ALREADY_CONNECTED') {
		return (msg && msg.trim()) || JOIN_ERROR_MESSAGES.ROLE_ALREADY_CONNECTED
	}
	if (code === 'PARTICIPANT_LIMIT') {
		return (msg && msg.trim()) || JOIN_ERROR_MESSAGES.PARTICIPANT_LIMIT
	}
	if (code && JOIN_ERROR_MESSAGES[code]) return JOIN_ERROR_MESSAGES[code]

	if (status === 403 && /15 minutes|scheduled|not started/i.test(msg)) {
		return msg
	}
	return msg || 'Could not join the meeting.'
}

export function isBotIdentity(identity = '') {
	const lower = String(identity).toLowerCase()
	return lower.includes('peegu-ai') || lower.includes('ai-bot')
}

/** Super Admin Ghost Observer (LiveKit hidden / receive-only). */
export function isGhostIdentity(identity = '') {
	const lower = String(identity).toLowerCase()
	return lower.startsWith('ghost:') || lower.startsWith('observer:')
}

export function isVisibleParticipantIdentity(identity = '') {
	return !isBotIdentity(identity) && !isGhostIdentity(identity)
}

/**
 * Super Admin only — Ghost Observer token body.
 * Must call authenticated /get-observer-token (not get-video-token).
 */
export function buildGhostObserverExtra(user) {
	return {
		userId: user?._id,
		userPermissions: user?.permissions || [],
		isAdmin: isAdminUser(user),
		participantRole: 'ghost',
		mode: 'OBSERVER',
	}
}
